import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { handleError } from "../../utils/handleError";
import { Location, Locations } from "../../types/api/Location";
import { Globe } from "react-feather";
import cx from "classnames";
import { useGeocodingCore, useSearchBoxCore } from "@mapbox/search-js-react";
import {
  GeocodingCore,
  type SearchBoxFeatureSuggestion,
  type SearchBoxSuggestion,
} from "@mapbox/search-js-core";
import { formatLocation } from "../../shared/formatLocation";
import { SelectedFile } from "../../types/PostSelection";
import axios from "axios";

export function LocationSearch(props: {
  updateLocation: (location: Location) => void;
  initialLocation?: Location;
  aboveMaxLength: boolean;
  selectedFiles?: SelectedFile[];
}) {
  const [value, setValue] = useState<string | null>(null);
  // const [locations, setLocations] = useState<Locations>([]);
  const [filteredLocations, setFilteredLocations] = useState<
    SearchBoxSuggestion[]
  >([]);
  const searchBoxCore = useSearchBoxCore({
    accessToken: process.env.REACT_APP_MAPBOX_ACCESS_TOKEN,
  });

  const setSuggestionAsLocation = useCallback(
    (place: SearchBoxFeatureSuggestion, nameOverride?: string) => {
      const id = new Date().getTime();
      const suggestion: Location = {
        id,
        _id: `${id}`,
        name: nameOverride ?? place.properties.name ?? "",
        location_id: null,
        details: {
          zip: place.properties.context.postcode?.name ?? "",
          street: place.properties.context.street?.name ?? "",
          country: place.properties.context.country?.name ?? "",
          latitude: place.geometry.coordinates[1],
          longitude: place.geometry.coordinates[0],
          city: place.properties.context.place?.name ?? "",
          region:
            place.properties.context.region?.region_code ??
            place.properties.context.region?.name ??
            "",
          mapbox_id: place.properties.mapbox_id ?? "",
        },
        created_at: new Date().toISOString(),
      };

      setValue(formatLocation(suggestion, false /* isEmbedded */));
      props.updateLocation(suggestion);
    },
    [props, setValue]
  );

  useEffect(() => {
    const aysncFct = async () => {
      if (props.initialLocation && value === null) {
        setValue(formatLocation(props.initialLocation));
      } else if (
        props.selectedFiles &&
        props.selectedFiles.length > 0 &&
        value === null
      ) {
        console.log(
          "[GEODECODE] SELECTED FILES FOR GEOCODE",
          props.selectedFiles
        );

        const firstFileWithLocationHint = props.selectedFiles.find(
          (file) => file.locationHint && file.locationHint.LatLng
        );

        const locationHint = firstFileWithLocationHint?.locationHint;
        let latLng = locationHint?.LatLng;

        if (!locationHint || !latLng?.[0] || !latLng?.[1]) {
          console.warn(
            "[GEODECODE] No valid LatLng found in location hint, using first file's location hint.",
            locationHint
          );
          return; // Exit early if no valid coordinates
        }

        let latLngValues: [number, number] = [0, 0];

        console.log("[GEODECODE] LOCATION HINT", locationHint, latLng);

        if (
          locationHint?.GPSMapDatum === "WGS-84" &&
          latLng &&
          locationHint.GPSLatitudeRef &&
          locationHint.GPSLongitudeRef
        ) {
          latLngValues = convertWSG84ToLatLng(
            latLng as [number[], number[]],
            locationHint.GPSLatitudeRef,
            locationHint.GPSLongitudeRef
          );

          console.log(
            "[GEODECODE] LOCATION CONVERTED WSG84 TO LAT LNG",
            latLngValues
          );
        } else if (Array.isArray(latLng)) {
          // Handle different coordinate formats
          let lat: number;
          let lng: number;

          console.log("[GEODECODE] RAW LAT LNG", latLng);

          // Extract latitude (first element)
          if (typeof latLng[0] === "string") {
            lat = parseFloat(latLng[0]);
          } else if (Array.isArray(latLng[0])) {
            // If it's an array, take the first element
            lat = toDecimalDegrees(latLng[0] as [number, number]);
          } else {
            lat = latLng[0];
          }

          // Extract longitude (second element)
          if (typeof latLng[1] === "string") {
            lng = parseFloat(latLng[1]);
          } else if (Array.isArray(latLng[1])) {
            // If it's an array, take the first element
            lng = toDecimalDegrees(latLng[1] as [number, number]);
          } else {
            lng = latLng[1];
          }

          // Check if coordinates are valid numbers
          if (isNaN(lat) || isNaN(lng)) {
            console.warn("[GEODECODE] Invalid coordinates found:", {
              lat,
              lng,
              original: latLng,
            });
            return; // Exit early if coordinates are invalid
          }

          latLngValues = [
            (locationHint.GPSLongitudeRef === "E" ? 1 : -1) * lng,
            (locationHint.GPSLatitudeRef === "N" ? 1 : -1) * lat,
          ]; // Note: Mapbox expects [longitude, latitude]

          console.log("[GEODECODE] LOCATION EXTRACTED LAT LNG", latLngValues, {
            lat,
            lng,
          });
        }

        // Final validation before making the API call
        if (
          isNaN(latLngValues[0]) ||
          isNaN(latLngValues[1]) ||
          latLngValues[0] === 0 ||
          latLngValues[1] === 0
        ) {
          console.warn(
            "[GEODECODE] Invalid or zero latLngValues computed:",
            latLngValues
          );
          return;
        }

        console.log("[GEODECODE] LAT LNG VALUES", latLngValues);

        const suggestions = await searchBoxCore.reverse(latLngValues, {
          types: "locality,place,region",
        });

        console.log("[GEODECODE] REVERSE GEOCODE SUGGESTIONS", suggestions);

        if (
          !suggestions ||
          !suggestions.features ||
          suggestions.features.length === 0
        ) {
          console.warn(
            "[GEODECODE] No suggestions found for reverse geocode.",
            suggestions
          );
          return;
        }

        const { data } = await axios.get(
          `https://api.mapbox.com/v4/mapbox.mapbox-streets-v8,mapbox.mapbox-terrain-v2/tilequery/${latLngValues.join(
            ","
          )}.json?radius=25&limit=5&dedupe&access_token=${
            process.env.REACT_APP_MAPBOX_ACCESS_TOKEN
          }`
        );

        let nationalParkName: string | undefined = undefined;
        if (data && data.features && data.features.length > 0) {
          const nationalParks = data.features.filter(
            (f: any) => f.properties?.class === "national_park"
          );

          if (nationalParks.length > 0) {
            console.log("[GEODECODE] NATIONAL PARKS FOUND", nationalParks);
            nationalParkName = nationalParks[0].properties.name;
          }

          console.log("[GEODECODE] GET POINTS OF INTEREST", data.features);
        }

        setSuggestionAsLocation(
          suggestions.features[0] as SearchBoxFeatureSuggestion,
          nationalParkName
        );
      }
    };
    aysncFct();
  }, [
    props.initialLocation,
    value,
    props.selectedFiles,
    searchBoxCore,
    setSuggestionAsLocation,
  ]);

  const search = async (event: ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setValue(query);

    try {
      if (!query || query.length === 0) {
        setFilteredLocations([]);
        return;
      }

      const response = await searchBoxCore.suggest(query, {
        sessionToken: "test-123",
      });

      console.log("[GEODECODE] LOCATION SEARCH RESPONSE", response);

      setFilteredLocations(response.suggestions);
    } catch (e) {
      handleError(e as Error);
    }
  };

  const selectLocation = async (location: SearchBoxSuggestion) => {
    const elem = document.activeElement as HTMLInputElement;
    if (elem) {
      elem?.blur();
    }

    const results = await searchBoxCore.retrieve(location, {
      sessionToken: "test-123",
    });

    console.log("[GEODECODE] SELECTED LOCATION", results);

    if (!results || !results.features || !results.features[0]) {
      console.error(
        "[GEODECODE] No results found for the selected location.",
        results
      );
      return;
    }

    const place = results.features[0];

    setSuggestionAsLocation(place);
  };

  return (
    <div className="form-control w-full">
      <div className="dropdown w-full">
        <label
          className={cx("input w-full", props.aboveMaxLength && "input-error")}
          tabIndex={0}
        >
          <span className="label">
            <Globe size={16} />
          </span>
          <input
            type="text"
            placeholder="Location search"
            className="grow"
            onChange={search}
            value={value ?? ""}
            autoComplete="off"
          />
        </label>

        <ul
          tabIndex={0}
          className="dropdown-content menu menu-sm bg-base-300 rounded-box z-1 w-full p-2 shadow-sm"
        >
          {filteredLocations.map((l) => (
            <li>
              <button
                className="prose flex-col cursor-pointer hover:bg-base-300"
                onClick={() => selectLocation(l)}
                key={l.place_formatted}
                type="button"
              >
                <div>
                  <span className="text-sm">{l.name}</span> <br />
                  <span className="text-xs">{l.place_formatted}</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function toDecimalDegrees([deg, min]: [number, number]): number {
  return deg + min / 60;
}

function convertWSG84ToLatLng(
  wsg84: [number[], number[]],
  latRef: string,
  lngRef: string
): [number, number] {
  // Validate input arrays
  if (
    !Array.isArray(wsg84[0]) ||
    !Array.isArray(wsg84[1]) ||
    wsg84[0].length < 3 ||
    wsg84[1].length < 3
  ) {
    console.warn("Invalid WSG84 coordinate format:", wsg84);
    return [0, 0];
  }

  const lat =
    (latRef === "N" ? 1 : -1) *
    (wsg84[0][0] + wsg84[0][1] / 60 + wsg84[0][2] / 3600);

  const lng =
    (lngRef === "E" ? 1 : -1) *
    (wsg84[1][0] + wsg84[1][1] / 60 + wsg84[1][2] / 3600);

  // Validate computed coordinates
  if (isNaN(lat) || isNaN(lng)) {
    console.warn("Computed NaN coordinates from WSG84:", { lat, lng, wsg84 });
    return [0, 0];
  }

  return [lng, lat]; // Return [longitude, latitude] for Mapbox
}
